import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP, setCookie } from "@tanstack/react-start/server";
import { z } from "zod";

import {
  partnerSession,
  requireAdminOrRop,
  requireDocflow,
  requirePartner,
} from "@/auth/middleware";
import { requestPortal } from "@/auth/session";
import { getLucia } from "@/auth/lucia";
import { partnersBaseUrl } from "@/auth/portal";
import { LoginThrottle, waitText } from "@/server/login-throttle";
import { afterRequest, runInUserContext } from "@/db/client";
import {
  hashInviteToken,
  inviteUrl,
  looksLikeToken,
  passwordProblem,
  PARTNER_KINDS,
  type PartnerRow,
} from "./partners";
import {
  acceptInvite,
  archivePartner,
  createPartner,
  listPartners,
  peekInvite,
  changeMyRequisites,
  changePartnerRequisites,
  dropFile,
  listFiles,
  readFile,
  readMyCard,
  readPartnerCard,
  existingNameKeys,
  insertListed,
  inviteListed,
  replaceFile,
  type StoredFileMeta,
  reissueInvite,
  restorePartner,
  setTaxMarkup,
  logPdAccess,
  readPdLog,
  type PdAccess,
  updatePartner,
  type InvitePeek,
  type PartnerCard,
} from "./partners-store";
import {
  dropDoc,
  recordDoc,
  queueNpd,
  recordRegistry,
  review,
  saveDraft,
  staffEdit,
  submit,
  type Requisites,
  type Step,
} from "./partner-requisites";
import { runRegistry } from "./registry-fetch";
import { kickNpdQueue } from "./npd-queue";
import { notifyLeadsSubmitted, notifyPartnerReview } from "./partner-notify";
import { botUsername, ensureBot } from "./telegram-bot";
import { issueCode, readLink, unlink, type TelegramLink } from "./telegram-link";
import { collectCandidates, nameKey, type ImportCandidate } from "./partner-import";
import { readSheetRows } from "./partner-import-sheet";
import { ACCEPTED_MIME, MAX_FILE_BYTES } from "./partner-docs";
import { DOCS, type Values } from "./partner-form";
import { recognizeDocument } from "./partner-recognize";
import { checkAndConsume } from "@/server/rate-limit";

/**
 * Подрядчики: экран сотрудника, приглашение по ссылке и кабинет партнёра.
 *
 * Три круга доступа, и у каждого свой замок:
 *  - экран «Подрядчики» — администратор, руководитель и менеджер по
 *    документам (`requireDocflow`);
 *  - приглашение — без входа, но только на поддомене партнёров и только по
 *    живой ссылке, которую нельзя подобрать;
 *  - кабинет — подрядчик на своём поддомене (`requirePartner`).
 */

/** Адрес кабинета для ссылок; `null` — не задан PARTNERS_URL. */
function partnersBase(): string | null {
  return partnersBaseUrl({
    PARTNERS_URL: process.env.PARTNERS_URL,
    PUBLIC_URL: process.env.PUBLIC_URL,
  });
}

export interface PartnersView {
  rows: PartnerRow[];
  /** куда ведут ссылки; пусто — ссылку выдать некуда, экран скажет почему */
  base: string | null;
}

export const loadPartners = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .handler(
    async (): Promise<PartnersView> => ({ rows: await listPartners(), base: partnersBase() }),
  );

const Kind = z.enum(PARTNER_KINDS as [string, ...string[]]) as z.ZodType<PartnerRow["kind"]>;

export type InviteResult = { ok: true; url: string } | { ok: false; error: string };

function linkOrError(token: string): InviteResult {
  const base = partnersBase();
  if (!base) {
    return {
      ok: false,
      error: "Не задан адрес кабинета партнёров (PARTNERS_URL) — ссылку выдать некуда.",
    };
  }
  return { ok: true, url: inviteUrl(base, token) };
}

export const invitePartner = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z
      .object({
        name: z.string().trim().min(1).max(200),
        kind: Kind,
        email: z.string().trim().email().max(320),
        note: z.string().max(2000).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }): Promise<InviteResult> => {
    // Без адреса кабинета подрядчика не заводим: ссылку всё равно не выдать.
    if (!partnersBase()) return linkOrError("");
    const res = await createPartner(data);
    return res.ok ? linkOrError(res.token) : res;
  });

export const updatePartnerFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        name: z.string().trim().min(1).max(200),
        kind: Kind,
        note: z.string().max(2000).optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => updatePartner(data.id, data));

const ById = (data: unknown) => z.object({ id: z.string().uuid() }).parse(data);

export const reissueInviteFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator(ById)
  .handler(async ({ data }): Promise<InviteResult> => {
    if (!partnersBase()) return linkOrError("");
    const res = await reissueInvite(data.id);
    return res.ok ? linkOrError(res.token) : res;
  });

export const archivePartnerFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator(ById)
  .handler(async ({ data }) => archivePartner(data.id));

export const restorePartnerFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator(ById)
  .handler(async ({ data }) => restorePartner(data.id));

// ---------------------------------------------------------------------
// Приглашение. Без входа — поэтому только на поддомене партнёров, только с
// токеном правильной формы и с тем же ограничением частоты, что у входа:
// перебирать ссылки не дешевле, чем пароли.
// ---------------------------------------------------------------------

const inviteThrottle = new LoginThrottle();

/**
 * Ключ ограничителя — сама ссылка (её хеш), а не общая строка: раньше восемь
 * мёртвых ссылок с любых адресов закрывали приём приглашений всем на
 * пятнадцать минут (аудит 15.10, находка 6). Перебор по-прежнему держит
 * лимит адреса.
 */
async function inviteKey(token: string): Promise<string> {
  return `invite:${(await hashInviteToken(token)).slice(0, 16)}`;
}

function clientIp(): string | null {
  return getRequestHeader("x-real-ip")?.trim() || getRequestIP() || null;
}

const Token = z.object({ token: z.string().max(100) });

export type PeekResult =
  | { ok: true; invite: InvitePeek }
  | { ok: false; reason: "invalid" | "throttled"; wait?: number };

// POST, а не GET: токен — это пароль, и в строке запроса он оседал бы в
// журналах (аудит 15.10, находка 16). Страница берёт его из `#` ссылки,
// который браузер на сервер не шлёт вовсе.
export const peekInviteFn = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => Token.parse(data))
  .handler(async ({ data }): Promise<PeekResult> => {
    if (requestPortal() !== "partners" || !looksLikeToken(data.token)) {
      return { ok: false, reason: "invalid" };
    }
    const ip = clientIp();
    const key = await inviteKey(data.token);
    const wait = inviteThrottle.attempt(key, ip);
    if (wait > 0) return { ok: false, reason: "throttled", wait };
    const peek = await peekInvite(data.token);
    if (!peek) return { ok: false, reason: "invalid" };
    inviteThrottle.succeed(key, ip);
    return { ok: true, invite: peek };
  });

export const acceptInviteFn = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    Token.extend({ password: z.string().min(1).max(1024) }).parse(data),
  )
  .handler(async ({ data }): Promise<{ ok: true } | { ok: false; error: string }> => {
    if (requestPortal() !== "partners") {
      return { ok: false, error: "Приглашение открывается в кабинете партнёра." };
    }
    if (!looksLikeToken(data.token)) {
      return { ok: false, error: "Ссылка недействительна или устарела" };
    }
    const ip = clientIp();
    const key = await inviteKey(data.token);
    const wait = inviteThrottle.attempt(key, ip);
    if (wait > 0)
      return { ok: false, error: `Слишком много попыток. Попробуйте через ${waitText(wait)}.` };
    const peek = await peekInvite(data.token);
    if (!peek) return { ok: false, error: "Ссылка недействительна или устарела" };
    inviteThrottle.succeed(key, ip);
    const problem = passwordProblem(data.password, peek.email);
    if (problem) return { ok: false, error: problem };

    const res = await acceptInvite(data.token, data.password);
    if (!res.ok) return res;

    const lucia = getLucia();
    const session = await lucia.createSession(res.userId, {});
    const cookie = lucia.createSessionCookie(session.id);
    setCookie(cookie.name, cookie.value, cookie.attributes);
    return { ok: true };
  });

// ---------------------------------------------------------------------
// Кабинет партнёра.
// ---------------------------------------------------------------------

export const loadMyCard = createServerFn({ method: "GET" })
  .middleware([requirePartner])
  .handler(async (): Promise<PartnerCard | null> => readMyCard());

/** Значения анкеты: ключи и длина ограничены, лишнее отбросит нормализация. */
const ValuesSchema = z
  .record(z.string().max(60), z.string().max(1000))
  .refine((v) => Object.keys(v).length <= 80, "Слишком много полей");

export const saveMyRequisites = createServerFn({ method: "POST" })
  .middleware([requirePartner])
  .inputValidator((data: unknown) => z.object({ values: ValuesSchema }).parse(data))
  .handler(
    async ({ data }): Promise<Step<PartnerCard>> =>
      changeMyRequisites((prev, kind) =>
        saveDraft(prev, kind, data.values, new Date().toISOString()),
      ),
  );

/**
 * Отправок на подрядчика в сутки. Каждая — запрос в DaData (бесплатно 10 000
 * в сутки на всех): без предела один человек скриптом «поправил символ —
 * отправил» выбирал его за ночь (аудит 15.10, находка 12). В памяти
 * процесса, как и лимит входа; от сброса выкаткой страхует кэш сверки ниже.
 */
const DAILY_SUBMITS = 20;
const submits = new Map<string, number[]>();

function takeDaily(store: Map<string, number[]>, key: string, limit: number): boolean {
  const since = Date.now() - 24 * 3_600_000;
  const list = (store.get(key) ?? []).filter((t) => t > since);
  if (list.length >= limit) return false;
  store.set(key, [...list, Date.now()]);
  return true;
}

/** Сверка по тому же ИНН моложе суток — в DaData не ходим снова. */
function freshRegistry(r: Requisites, inn: string | undefined): boolean {
  const reg = r.registry;
  if (!reg?.party || reg.errors?.length || !inn || reg.inn !== inn) return false;
  return Date.now() - Date.parse(reg.checkedAt) < 24 * 3_600_000;
}

export const submitMyRequisites = createServerFn({ method: "POST" })
  .middleware([partnerSession])
  .inputValidator((data: unknown) => z.object({ agreed: z.boolean() }).parse(data))
  .handler(async ({ data, context }): Promise<Step<PartnerCard>> => {
    const me = context.userId;
    if (!takeDaily(submits, me, DAILY_SUBMITS)) {
      return { ok: false, error: "Слишком много отправок за сутки — попробуйте завтра" };
    }
    const sent = await runInUserContext(me, () =>
      changeMyRequisites((prev, kind) =>
        submit(prev, kind, { agreed: data.agreed, ip: clientIp() }, new Date().toISOString()),
      ),
    );
    if (!sent.ok) return sent;
    const { id, name, kind } = sent.next;
    afterRequest("partner-notify", () => notifyLeadsSubmitted(id, name));
    // Самозанятый — в очередь на проверку в ФНС: ответа там ждать до минуты,
    // держать человека на кнопке столько нельзя.
    if (kind === "self_employed") {
      const q = await runInUserContext(me, () =>
        changeMyRequisites((prev) => queueNpd(prev, new Date().toISOString())),
      );
      kickNpdQueue(2_000);
      return q.ok ? q : sent;
    }
    // Сверка с реестрами — сразу при отправке, чтобы Даша открыла карточку
    // уже с ответом. Сеть — вне транзакции. Реестр не ответил — отправка всё
    // равно состоялась, а причина ляжет в сверку предупреждением.
    const values = sent.next.requisites.values;
    if (freshRegistry(sent.next.requisites, values.inn)) return sent;
    const registry = await runRegistry(kind, values);
    if (!registry) return sent;
    const checked = await runInUserContext(me, () =>
      changeMyRequisites((prev, k) => recordRegistry(prev, k, registry)),
    );
    return checked.ok ? checked : sent;
  });

// ---------------------------------------------------------------------
// Карточка подрядчика у Даши: посмотреть реквизиты, принять или вернуть.
// ---------------------------------------------------------------------

export const loadPartnerCard = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .inputValidator(ById)
  .handler(async ({ data, context }): Promise<PartnerCard | null> => {
    const card = await readPartnerCard(data.id);
    if (card) await logPdAccess(card.id, context.auth.email, "открыл карточку");
    return card;
  });

/** Журнал доступа к данным подрядчика — администратору и руководителю. */
export const loadPdLog = createServerFn({ method: "GET" })
  .middleware([requireAdminOrRop])
  .inputValidator(ById)
  .handler(async ({ data }): Promise<PdAccess[]> => readPdLog(data.id));

export const reviewPartnerFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z
      .object({
        id: z.string().uuid(),
        decision: z.enum(["approve", "return"]),
        note: z.string().max(2000).optional(),
        /** `reviewKey` карточки, которую Даша видит на экране */
        seen: z.string().max(200_000),
      })
      .parse(data),
  )
  .handler(async ({ data, context }): Promise<Step<PartnerCard>> => {
    const res = await changePartnerRequisites(data.id, (prev) =>
      review(
        prev,
        data.decision,
        context.auth.email,
        data.note,
        new Date().toISOString(),
        data.seen,
      ),
    );
    if (res.ok) {
      await logPdAccess(
        data.id,
        context.auth.email,
        data.decision === "approve" ? "принял реквизиты" : "вернул реквизиты",
      );
      const userId = res.next.userId;
      afterRequest("partner-notify", () => notifyPartnerReview(userId, data.decision, data.note));
    }
    return res;
  });

/** Даша вписывает реквизиты за подрядчика — того, кто сам не заполнит. */
export const editPartnerRequisitesFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z
      .object({ id: z.string().uuid(), values: ValuesSchema, seen: z.string().max(200_000) })
      .parse(data),
  )
  .handler(async ({ data, context }): Promise<Step<PartnerCard>> => {
    const res = await changePartnerRequisites(data.id, (prev, kind) =>
      staffEdit(prev, kind, data.values, context.auth.email, new Date().toISOString(), data.seen),
    );
    if (res.ok) await logPdAccess(data.id, context.auth.email, "вписал реквизиты");
    return res;
  });

/**
 * Налог подрядчика «сверху», %: сумма «на руки» плюс налог по его ставке
 * (ответ Даши 21.10). Пусто — не задан.
 */
export const setTaxMarkupFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), pct: z.number().min(0).max(30).nullable() }).parse(data),
  )
  .handler(async ({ data }): Promise<PartnerCard | null> => setTaxMarkup(data.id, data.pct));

// ---------------------------------------------------------------------
// Документы подрядчика: загрузка с распознаванием (этап 2б).
//
// Файл проверяется по содержимому, а не по заявленному типу: подпись
// «image/png» у произвольных байтов модель бы честно попыталась прочитать.
// Паспорт проходит через память и модель и не пишется никуда (решение
// 14.10); справки и карточки сохраняются — они нужны бухгалтерии.
// ---------------------------------------------------------------------

function sniff(bytes: Buffer): (typeof ACCEPTED_MIME)[number] | null {
  if (bytes.subarray(0, 4).toString("latin1") === "%PDF") return "application/pdf";
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  if (bytes.subarray(0, 8).equals(Buffer.from([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))) {
    return "image/png";
  }
  if (
    bytes.subarray(0, 4).toString("latin1") === "RIFF" &&
    bytes.subarray(8, 12).toString("latin1") === "WEBP"
  ) {
    return "image/webp";
  }
  return null;
}

/** Распознаваний на подрядчика в сутки: десяток документов с переснятыми. */
const DAILY_RECOGNITIONS = 40;
/** На всех подрядчиков в сутки — потолок расхода на модель. */
const DAILY_RECOGNITIONS_ALL = 400;
/** Распознаваний одновременно на весь сервер. */
const PARALLEL_RECOGNITIONS = 4;
/** Страниц в PDF: справке и карточке хватает пары, дальше — лишнее на модель. */
const MAX_PDF_PAGES = 15;

const recognitions = new Map<string, number[]>();
const inFlight = new Set<string>();

/** Страницы PDF по словарям `/Type /Page` — грубо, но без разбора файла. */
function pdfPages(bytes: Buffer): number {
  return (bytes.toString("latin1").match(/\/Type\s*\/Page(?![s\w])/g) ?? []).length;
}

export type UploadResult =
  | { ok: true; card: PartnerCard; found: Values }
  | { ok: false; error: string };

export const uploadMyDoc = createServerFn({ method: "POST" })
  .middleware([partnerSession])
  .inputValidator((data: unknown) =>
    z
      .object({
        docKind: z.string().max(40),
        name: z.string().min(1).max(200),
        base64: z
          .string()
          .min(1)
          .max(Math.ceil((MAX_FILE_BYTES * 4) / 3) + 16),
      })
      .parse(data),
  )
  .handler(async ({ data, context }): Promise<UploadResult> => {
    const me = context.userId;
    const card = await runInUserContext(me, () => readMyCard());
    if (!card) return { ok: false, error: "Карточка подрядчика не найдена" };
    const spec = DOCS[card.kind].find((d) => d.key === data.docKind);
    if (!spec) return { ok: false, error: "Такой документ для вашего оформления не нужен" };

    const bytes = Buffer.from(data.base64, "base64");
    if (bytes.length > MAX_FILE_BYTES) return { ok: false, error: "Файл больше 10 МБ" };
    const mime = sniff(bytes);
    if (!mime) return { ok: false, error: "Нужен PDF или фото (JPG, PNG, WebP)" };
    if (mime === "application/pdf" && pdfPages(bytes) > MAX_PDF_PAGES) {
      return { ok: false, error: `В файле больше ${MAX_PDF_PAGES} страниц — оставьте нужные` };
    }

    if (inFlight.has(me)) {
      return { ok: false, error: "Предыдущий документ ещё читается — подождите несколько секунд" };
    }
    if (inFlight.size >= PARALLEL_RECOGNITIONS) {
      return { ok: false, error: "Сейчас много загрузок — попробуйте через минуту" };
    }
    const limit = checkAndConsume(me, "partnerRecognize");
    if (
      !limit.ok ||
      !takeDaily(recognitions, me, DAILY_RECOGNITIONS) ||
      !takeDaily(recognitions, "*", DAILY_RECOGNITIONS_ALL)
    ) {
      return { ok: false, error: "Слишком много загрузок за сутки — попробуйте завтра" };
    }

    inFlight.add(me);
    let res: Awaited<ReturnType<typeof recognizeDocument>>;
    try {
      // В модель — ровно те байты, что проверены и будут сохранены, а не
      // входная строка: хвост после `=` декодер отбрасывает молча (находка 18).
      res = await recognizeDocument(card.kind, data.docKind, {
        name: data.name,
        mime,
        base64: bytes.toString("base64"),
      });
    } finally {
      inFlight.delete(me);
    }
    if (!res.ok) return res;
    const found = res;

    // Файл и отметка о нём — одной транзакцией: не записалась отметка —
    // откатывается и файл (раньше файл оставался новым, а зелёные отметки —
    // от прежнего, находка 9).
    try {
      const step = await runInUserContext(me, async () => {
        await replaceFile(card.id, data.docKind, { name: data.name, mime, bytes });
        const s = await changeMyRequisites((prev) =>
          recordDoc(prev, data.docKind, {
            at: new Date().toISOString(),
            fileName: data.name,
            fields: found.fields,
            docDate: found.docDate,
            stored: true,
          }),
        );
        if (!s.ok) throw new StepFailed(s.error);
        return s;
      });
      return { ok: true, card: step.next, found: found.fields };
    } catch (e) {
      if (e instanceof StepFailed) return { ok: false, error: e.message };
      throw e;
    }
  });

/** Переход отказал внутри транзакции — откатить её и вернуть причину. */
class StepFailed extends Error {}

export const removeMyDoc = createServerFn({ method: "POST" })
  .middleware([requirePartner])
  .inputValidator((data: unknown) => z.object({ docKind: z.string().max(40) }).parse(data))
  .handler(async ({ data }): Promise<Step<PartnerCard>> => {
    const card = await readMyCard();
    if (!card) return { ok: false, error: "Карточка подрядчика не найдена" };
    await dropFile(card.id, data.docKind);
    const step = await changeMyRequisites((prev) =>
      dropDoc(prev, data.docKind, new Date().toISOString()),
    );
    // Отметка не снялась — файл возвращается откатом.
    if (!step.ok) throw new Response(step.error, { status: 409 });
    return step;
  });

export const loadPartnerFiles = createServerFn({ method: "GET" })
  .middleware([requireDocflow])
  .inputValidator(ById)
  .handler(async ({ data }): Promise<StoredFileMeta[]> => listFiles(data.id));

export const loadPartnerFile = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), docKind: z.string().max(40) }).parse(data),
  )
  .handler(
    async ({ data, context }): Promise<{ name: string; mime: string; base64: string } | null> => {
      const f = await readFile(data.id, data.docKind);
      if (f) await logPdAccess(data.id, context.auth.email, `скачал: ${f.name}`);
      return f ? { name: f.name, mime: f.mime, base64: f.bytes.toString("base64") } : null;
    },
  );

/**
 * Даша: сверить ещё раз — после правки или когда задали ключ. ИП и ООО
 * сверяются с реестром сразу; самозанятый встаёт в очередь ФНС.
 */
export const checkPartnerRegistry = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator(ById)
  .handler(async ({ data }): Promise<Step<PartnerCard>> => {
    const card = await readPartnerCard(data.id);
    if (!card) return { ok: false, error: "Подрядчик не найден" };
    if (card.kind === "self_employed") {
      const q = await changePartnerRequisites(data.id, (prev) =>
        queueNpd(prev, new Date().toISOString()),
      );
      kickNpdQueue(2_000);
      return q;
    }
    const registry = await runRegistry(card.kind, card.requisites.values);
    if (!registry) return { ok: false, error: "Сверка не прошла" };
    return changePartnerRequisites(data.id, (prev) => recordRegistry(prev, card.kind, registry));
  });

// ---------------------------------------------------------------------
// Импорт подрядчиков из таблицы Даши (xlsx). Сначала просмотр — что
// нашлось, кто уже есть, — потом добавление выбранных «в список».
// ---------------------------------------------------------------------

export interface ImportPreview {
  sheets: string[];
  rows: number;
  candidates: (ImportCandidate & { exists: boolean })[];
}

export const previewImport = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z.object({ base64: z.string().min(1).max(30_000_000) }).parse(data),
  )
  .handler(
    async ({
      data,
    }): Promise<{ ok: true; preview: ImportPreview } | { ok: false; error: string }> => {
      let parsed: Awaited<ReturnType<typeof readSheetRows>>;
      try {
        parsed = await readSheetRows(Buffer.from(data.base64, "base64"));
      } catch (e) {
        console.warn("[import] таблица не прочиталась", e);
        return { ok: false, error: "Не удалось прочитать файл — нужна таблица .xlsx" };
      }
      if (!parsed.sheets.length) {
        return { ok: false, error: "Не нашлось листа с колонками «Подрядчик» и «Юр. лицо»" };
      }
      const have = await existingNameKeys();
      const candidates = collectCandidates(parsed.rows).map((c) => ({
        ...c,
        exists: have.has(nameKey(c.name)),
      }));
      return { ok: true, preview: { sheets: parsed.sheets, rows: parsed.rows.length, candidates } };
    },
  );

const Candidate = z.object({
  key: z.string().max(300),
  name: z.string().trim().min(1).max(200),
  kind: Kind,
  kindGuessed: z.boolean(),
  telegram: z.string().max(100).optional(),
  rows: z.number().int().min(0).max(100_000),
  lastProject: z.string().max(300).optional(),
  executors: z.array(z.string().max(200)).max(20),
});

export const applyImport = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) => z.object({ items: z.array(Candidate).max(2000) }).parse(data))
  .handler(
    async ({ data }): Promise<{ ok: true; added: number }> => ({
      ok: true,
      added: await insertListed(data.items as ImportCandidate[]),
    }),
  );

export const inviteListedFn = createServerFn({ method: "POST" })
  .middleware([requireDocflow])
  .inputValidator((data: unknown) =>
    z.object({ id: z.string().uuid(), email: z.string().trim().email().max(320) }).parse(data),
  )
  .handler(async ({ data }): Promise<InviteResult> => {
    if (!partnersBase()) return linkOrError("");
    const res = await inviteListed(data.id, data.email);
    return res.ok ? linkOrError(res.token) : res;
  });

// ---------------------------------------------------------------------
// Телеграм подрядчика — тот же бот и тот же код привязки, что у
// сотрудников. Сюда приходит «реквизиты вернули / проверены», дальше —
// документы на согласование.
// ---------------------------------------------------------------------

export interface PartnerTelegram extends TelegramLink {
  bot: string | null;
}

export const loadPartnerTelegram = createServerFn({ method: "GET" })
  .middleware([requirePartner])
  .handler(async ({ context }): Promise<PartnerTelegram> => {
    ensureBot();
    const [link, bot] = await Promise.all([readLink(context.userId), botUsername()]);
    return { ...link, bot };
  });

export const linkPartnerTelegram = createServerFn({ method: "POST" })
  .middleware([requirePartner])
  .handler(async ({ context }): Promise<{ code: string; bot: string | null }> => {
    ensureBot();
    const [code, bot] = await Promise.all([issueCode(context.userId), botUsername()]);
    return { code, bot };
  });

export const unlinkPartnerTelegram = createServerFn({ method: "POST" })
  .middleware([requirePartner])
  .handler(async ({ context }): Promise<{ ok: true }> => {
    await unlink(context.userId);
    return { ok: true };
  });
